import { Params } from '@angular/router';
import { IgMedia } from '@core/models/ApiFields/ig-entities';
import {
    IgMetadataAgeEnum,
    IgMetadataColorEnum,
    IgMetadataEmotionEnum,
    IgMetadataGenderEnum,
    IgMetadataLabelEnum,
} from '@core/models/ApiFields/ig-entities.enums';
import { Paginated } from '@core/models/IApi';
import {
    IG_ENDPOINT,
    metadataFields,
} from '@features/social-analysis/social-listening/config/api-params';
import { MetadataState } from '@features/social-analysis/social-shared/store/post-metadata.store';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { HttpBuilder } from '../../social-shared/services/http/http-builder';

type Http$ = Observable<Paginated>;
type HttpArr = Record<string, Http$>;

export class PostMetadataHttpBuilder extends HttpBuilder {
    #entities = [
        IgMetadataColorEnum.prefix,
        IgMetadataLabelEnum.prefix,
        IgMetadataEmotionEnum.prefix,
        IgMetadataGenderEnum.prefix,
        IgMetadataAgeEnum.prefix,
    ];

    reqData(state: MetadataState, postId: string): void {
        const http$obj: HttpArr = this.#entities.reduce(
            (obj, entity) => ({ ...obj, [entity]: this.getHttp$(entity, postId) }),
            {}
        );

        Object.keys(http$obj).forEach(entity => this.makeReq(state, http$obj[entity], entity));
    }

    protected makeReq(state: MetadataState, http$: Http$, entity: string) {
        state.data.update(prev => ({ ...prev, [entity]: [] }));
        state.isLoading[entity].set(true);

        http$
            .pipe(
                map(res => this.formatMultiRes(res)),
                finalize(() => state.isLoading[entity].set(false))
            )
            .subscribe({
                next: (data: any) => state.data.update(prev => ({ ...prev, ...data })),
                error: err => this.toastr.error(err),
            });
    }

    protected getHttp$(entity: string, postId: string) {
        const url = this.buildUrl(IG_ENDPOINT);
        const params = this.buildParams(postId, entity);

        return this.http.get<Paginated>(url, { params: params });
    }

    protected buildParams(postId: string, entity: string) {
        const plusId = this.addId(postId);
        const plusParams = this.addParamsByEntity(entity, plusId);

        return plusParams;
    }

    protected addId(postId: string, params?: Params): Params {
        return { ...params, [IgMedia.getPrefixed('mediaId')]: 'eq:' + postId };
    }

    protected addParamsByEntity(entity: string, params?: Params): Params {
        const moreParams = metadataFields[entity];

        return { ...params, ...moreParams };
    }

    protected formatMultiRes(res: Paginated) {
        let keys: string[] = [];
        const firstItem = res.data[0];
        if (!firstItem) {
            return {};
        }

        keys = Object.keys(firstItem);

        return { [keys[0]]: res.data.map(item => this.flattenOneLevel(item)) };
    }
}
